"use client";

// New-chat hero for the COMMAND v2 prototype: headline + the full preset
// library as rows, with category pills on top (same filter set as the Presets
// Library page). Clicking a row fills the composer (handled by the parent).

import { useCallback, useEffect, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { Layers, Sparkles, Star } from "lucide-react";

import { cn } from "@/lib/utils";
import type { ProtoPreset, ProtoPresetCategory } from "../prototype/types";
import { CommandEmptyStatePresetSkeleton } from "./command-v2-loading-skeletons";
import {
  useCommandV2EmptyPresets,
  type CommandV2PresetFilter,
} from "@/hooks/use-command-v2-empty-presets";

export const PRESET_CATEGORY_META: Record<
  ProtoPresetCategory,
  { label: string; chipClass: string }
> = {
  executive: {
    label: "Executive",
    chipClass:
      "border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-900/40 dark:bg-violet-950/30 dark:text-violet-400",
  },
  management: {
    label: "Management",
    chipClass:
      "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/40 dark:bg-blue-950/30 dark:text-blue-400",
  },
  custom: {
    label: "Custom",
    chipClass:
      "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-400",
  },
};

export function ChainStepsBadge({
  count,
  className,
}: {
  count: number;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-semibold text-primary dark:bg-violet-950/40",
        className
      )}
    >
      <Layers className="size-3" />
      {count} steps
    </span>
  );
}

// ── Category filters ─────────────────────────────────────────────────────────
// Same values/order as the Presets Library page so the two read as one system.

type PresetFilter = CommandV2PresetFilter;

const PRESET_FILTERS: { value: PresetFilter; label: ReactNode }[] = [
  { value: "all", label: "All" },
  {
    value: "favourites",
    label: <Star className="size-3.5 fill-amber-400 text-amber-400" />,
  },
  { value: "executive", label: "Executive" },
  { value: "management", label: "Management" },
];

/** Rows fade out into the background at the scroll edges instead of clipping
 *  hard — top fade only once scrolled, bottom fade only while more is below. */
const FADE = "40px";
function edgeMask(top: boolean, bottom: boolean): CSSProperties {
  if (!top && !bottom) return {};
  const stops = [
    top ? `transparent 0, #000 ${FADE}` : "#000 0",
    bottom ? `#000 calc(100% - ${FADE}), transparent 100%` : "#000 100%",
  ].join(", ");
  const mask = `linear-gradient(to bottom, ${stops})`;
  return { maskImage: mask, WebkitMaskImage: mask };
}

function presetSubtitle(preset: ProtoPreset): string | null {
  const raw = (preset.description ?? preset.promptText ?? "").replace(/\s+/g, " ").trim();
  if (!raw) return null;
  return raw.length > 64 ? `${raw.slice(0, 64).trimEnd()}…` : raw;
}

const EMPTY_COPY: Record<PresetFilter, string> = {
  all: "No presets yet — save a prompt from the composer to start your library.",
  favourites: "No favourites yet — star a preset to pin it here.",
  custom: "No custom presets yet.",
  executive: "No executive presets yet.",
  management: "No management presets yet.",
};

export function CommandChatEmptyState({
  presets: fallbackPresets,
  onPickPreset,
  onToggleFavourite,
  isLoadingPresets,
}: {
  /** Fallback library when the category API has not returned yet. */
  presets?: ProtoPreset[];
  onPickPreset: (preset: ProtoPreset) => void;
  /** Star toggle on each row; omit to hide the stars. */
  onToggleFavourite?: (presetId: string) => void;
  isLoadingPresets?: boolean;
}) {
  const [filter, setFilter] = useState<PresetFilter>("all");
  const {
    presets: fetchedPresets,
    counts,
    isLoading: isLoadingFilter,
    hasNextPage,
    isFetchingNextPage,
    loadMoreRef,
  } = useCommandV2EmptyPresets(filter);

  const list = fetchedPresets.length > 0 ? fetchedPresets : fallbackPresets ?? [];

  // The category is already implied once a category pill is picked.
  const showCategoryChip = filter === "all" || filter === "favourites";

  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollHideTimerRef = useRef<number>(0);
  const [edges, setEdges] = useState({ top: false, bottom: false });
  const [isScrolling, setIsScrolling] = useState(false);

  const syncEdges = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const top = el.scrollTop > 4;
    const bottom = el.scrollTop + el.clientHeight < el.scrollHeight - 4;
    setEdges((current) =>
      current.top === top && current.bottom === bottom
        ? current
        : { top, bottom }
    );
  }, []);

  const handleScroll = useCallback(() => {
    syncEdges();
    setIsScrolling(true);
    window.clearTimeout(scrollHideTimerRef.current);
    scrollHideTimerRef.current = window.setTimeout(() => {
      setIsScrolling(false);
    }, 800);
  }, [syncEdges]);

  useEffect(() => {
    return () => window.clearTimeout(scrollHideTimerRef.current);
  }, []);

  // Re-check when the list changes (filter) and when the pane is resized.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    syncEdges();
    const observer = new ResizeObserver(syncEdges);
    observer.observe(el);
    return () => observer.disconnect();
  }, [syncEdges, list.length]);

  return (
    <div className="mx-auto flex h-full w-full min-h-0 max-w-4xl flex-1 flex-col px-4 py-8 sm:py-10">
      <div className="flex shrink-0 flex-col items-center">
        <div className="flex size-10 items-center justify-center rounded-xl bg-violet-100 dark:bg-violet-900/30">
          <Sparkles className="size-5 text-primary" />
        </div>
        <h1 className="mt-4 text-center text-xl font-semibold tracking-tight">
          What do you want to know or do?
        </h1>
        <p className="mt-1.5 max-w-md text-center text-sm text-muted-foreground">
          Ask your Command team for reports, analyses and next steps — or start
          from a preset.
        </p>
      </div>

      {/* Category pills — same styling as the Presets Library filter bar */}
      <div className="mx-auto mt-7 flex w-full max-w-3xl shrink-0 flex-wrap items-center justify-center gap-1.5">
        {PRESET_FILTERS.map((item) => (
          <button
            key={item.value}
            type="button"
            onClick={() => setFilter(item.value)}
            aria-pressed={filter === item.value}
            className={cn(
              "inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-xs font-medium transition-colors",
              filter === item.value
                ? "bg-foreground text-background dark:bg-zinc-700 dark:text-zinc-50"
                : "text-muted-foreground hover:bg-muted hover:text-foreground"
            )}
          >
            {item.label}
            <span
              className={cn(
                "text-[11px] tabular-nums",
                filter === item.value ? "opacity-70" : "opacity-60"
              )}
            >
              {counts[item.value]}
            </span>
          </button>
        ))}
      </div>

      {/* Preset rows — the only scrolling region; hero + pills stay put */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className={cn(
          "app-scrollbar scrollbar-reveal mx-auto mt-3 w-full min-h-0 max-w-3xl flex-1 overflow-y-auto",
          isScrolling && "is-scrolling"
        )}
      >
        <div style={edgeMask(edges.top, edges.bottom)}>
        {isLoadingPresets || isLoadingFilter ? (
          <CommandEmptyStatePresetSkeleton />
        ) : list.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-muted-foreground">
            {EMPTY_COPY[filter]}
          </p>
        ) : (
          <ul className="divide-y divide-border/60">
            {list.map((preset) => {
              const category = PRESET_CATEGORY_META[preset.category];
              const subtitle = presetSubtitle(preset);
              return (
                <li
                  key={preset.id}
                  className="group flex items-center gap-3 px-3 py-2.5 transition-colors hover:bg-muted/50 sm:px-4"
                >
                  {onToggleFavourite ? (
                    <button
                      type="button"
                      onClick={() => onToggleFavourite(preset.id)}
                      className="shrink-0"
                      aria-label={
                        preset.favourite
                          ? `Remove ${preset.title} from favourites`
                          : `Add ${preset.title} to favourites`
                      }
                      title={
                        preset.favourite ? "Remove favourite" : "Add to favourites"
                      }
                    >
                      <Star
                        className={cn(
                          "size-4 transition-colors",
                          preset.favourite
                            ? "fill-amber-400 text-amber-400"
                            : "text-muted-foreground/40 hover:text-muted-foreground"
                        )}
                      />
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => onPickPreset(preset)}
                    className="min-w-0 flex-1 text-left"
                  >
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-semibold group-hover:text-primary">
                        {preset.title}
                      </span>
                      {preset.chain ? (
                        <ChainStepsBadge count={preset.chain.steps.length} />
                      ) : null}
                    </div>
                    {subtitle ? (
                      <p className="mt-0.5 max-w-[36ch] truncate text-[13px] text-muted-foreground">
                        {subtitle}
                      </p>
                    ) : null}
                  </button>
                  {showCategoryChip ? (
                    <span
                      className={cn(
                        "hidden shrink-0 items-center rounded-full border px-2 py-0.5 text-[11px] font-medium sm:inline-flex",
                        category.chipClass
                      )}
                    >
                      {category.label}
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
        {hasNextPage ? (
          <div ref={loadMoreRef} className="py-3">
            {isFetchingNextPage ? <CommandEmptyStatePresetSkeleton /> : null}
          </div>
        ) : null}
        </div>
      </div>
    </div>
  );
}
